import { useEffect, type MutableRefObject } from 'react';
import type { Rnd } from 'react-rnd';
import { isKeyboardInsetActive } from '@/shared/lib/hooks';
import { resolveSnapBounds, type SnapZone } from '@/shared/lib/resolveSnapBounds';
import { resolveInitialBounds } from './resolveInitialBounds';
import { contentBoxSize } from './snapGeometry';

interface ParentResizeClampArgs {
  rndRef: MutableRefObject<Rnd | null>;
  parentSizeRef: MutableRefObject<{ width: number; height: number } | null>;
  maximizedRef: MutableRefObject<boolean>;
  snappedRef: MutableRefObject<boolean>;
  lastSnapZoneRef: MutableRefObject<SnapZone>;
  getRelativePosition: (el: HTMLElement) => { x: number; y: number };
  minWidth: number;
  minHeight: number;
}

/** 부모 크기가 바뀌면 maximize·snap·일반 창을 새 부모 기준으로 다시 맞춘다. */
export function useParentResizeClamp({
  rndRef,
  parentSizeRef,
  maximizedRef,
  snappedRef,
  lastSnapZoneRef,
  getRelativePosition,
  minWidth,
  minHeight,
}: ParentResizeClampArgs) {
  useEffect(() => {
    const parent = rndRef.current?.getSelfElement()?.parentElement;
    if (!parent) return;
    const update = () => {
      const size = contentBoxSize(parent);
      parentSizeRef.current = size;
      // #81: 부모가 줄어들면(orientation 전환·모바일·레이아웃 settle) 부모를 벗어난 윈도우를
      // 다시 부모 안으로 clamp 해 invisible 회귀를 막는다. in-bounds(사용자가 옮긴) 윈도우는
      // clamp 결과가 동일해 no-op.
      if (size.width <= 0 || size.height <= 0) return;
      const rnd = rndRef.current;
      const self = rnd?.getSelfElement();
      if (!rnd || !self) return;
      // K2: maximized/snapped 는 skip 대신 새 부모 기준 geometry 를 추적 재적용 —
      // skip 하면 부모 축소 시 full-surface/스냅 창이 부모를 넘친 채 고착된다.
      if (maximizedRef.current) {
        rnd.updatePosition({ x: 0, y: 0 });
        rnd.updateSize({ width: size.width, height: size.height });
        return;
      }
      if (snappedRef.current) {
        const bounds = resolveSnapBounds(lastSnapZoneRef.current, size, {
          width: minWidth,
          height: minHeight,
        });
        if (bounds) {
          rnd.updatePosition({ x: bounds.x, y: bounds.y });
          rnd.updateSize({ width: bounds.width, height: bounds.height });
        }
        return;
      }
      // Why: 가상 키보드로 셸이 일시 축소된 동안 재clamp 하면 창 높이가 영구히 줄고(clamp 는
      // 축소만 하고 부모가 돌아와도 복원하지 않는다) 키보드를 닫아도 복구되지 않는 단방향
      // 래칫이 된다. maximized/snapped 분기는 부모 기준으로 매번 재적용하므로 자기복원되고,
      // 이 clamp 분기만 비가역이라 인셋이 걸린 동안에만 보류한다.
      if (isKeyboardInsetActive()) return;
      const pos = getRelativePosition(self);
      const clamped = resolveInitialBounds(
        { x: pos.x, y: pos.y, width: self.offsetWidth, height: self.offsetHeight },
        size,
        { width: minWidth, height: minHeight }
      );
      if (
        clamped.x !== pos.x ||
        clamped.y !== pos.y ||
        clamped.width !== self.offsetWidth ||
        clamped.height !== self.offsetHeight
      ) {
        rnd.updatePosition({ x: clamped.x, y: clamped.y });
        rnd.updateSize({ width: clamped.width, height: clamped.height });
      }
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(parent);
    return () => ro.disconnect();
  }, [
    rndRef,
    parentSizeRef,
    maximizedRef,
    snappedRef,
    lastSnapZoneRef,
    getRelativePosition,
    minWidth,
    minHeight,
  ]);
}
